import { useEffect, useSyncExternalStore } from 'react'
import { fileSystemUrl, videoUrl } from '../constants/Connections'
import { getMediaToken } from '../server/server'

// One short-lived token shared by every file URL on the page. It is not the session token, so URLs
// that end up in logs, history or screenshots do not hand out the account.
const REFRESH_BEFORE_MS = 60 * 1000
const RETRY_MS = 15 * 1000

let current = null // { token, expiresAt }
let inflight = null
let timer = null
const listeners = new Set()

const emit = () => listeners.forEach((listener) => listener())

const schedule = (delay) => {
    clearTimeout(timer)
    timer = setTimeout(loadMediaToken, Math.max(delay, RETRY_MS))
}

const isValid = () => !!current && current.expiresAt > Date.now()

export async function loadMediaToken() {
    if (inflight) return inflight
    inflight = getMediaToken()
        .then((resp) => {
            if (resp?.status === 200 && resp.data?.token) {
                current = { token: resp.data.token, expiresAt: resp.data.expires_at * 1000 }
                schedule(current.expiresAt - Date.now() - REFRESH_BEFORE_MS)
                emit()
            } else if (localStorage.getItem('token')) {
                // Signed out: nothing to retry until the next sign-in mounts a component that needs it.
                schedule(RETRY_MS)
            }
        })
        .finally(() => {
            inflight = null
        })
    return inflight
}

// For code that is not a component (e.g. a fetch started from an event handler).
export async function ensureMediaToken() {
    if (!isValid()) await loadMediaToken()
    return current?.token || ''
}

// Called on logout: the next session asks for its own.
export function resetMediaToken() {
    clearTimeout(timer)
    timer = null
    current = null
    emit()
}

const subscribe = (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

const getSnapshot = () => (isValid() ? current.token : '')

// '' until the token has arrived; callers show no media for that moment.
export function useMediaToken() {
    const token = useSyncExternalStore(subscribe, getSnapshot)
    useEffect(() => {
        if (inflight) return
        if (!isValid()) loadMediaToken()
        else if (!timer) schedule(current.expiresAt - Date.now() - REFRESH_BEFORE_MS)
    }, [])
    return token
}

// Videos come from a CDN that does not check tokens, so they get none.
export const mediaUrl = (storageName, { token, isVideo = false, query = '' } = {}) => {
    if (!storageName) return ''
    if (isVideo) return `${videoUrl}/${storageName}`
    return token ? `${fileSystemUrl}/${storageName}?token=${token}${query}` : ''
}
